/**
 * HostelRateMaster.jsx — CAMP-RES-DEMO-01
 * Premium accommodation rate management — Kaveri Bhavan.
 * [DEMO] Illustrative INR tariffs — not actual VCE fee structure.
 */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import {
  Star, ArrowLeft, IndianRupee, Calendar, CheckCircle2,
  Clock, Shield, Utensils, Shirt, Wifi, Wind,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const VCE_INST = "44444444-4444-4444-4444-444444444444";

const CATEGORY_LABELS = {
  SUITE:    "Signature Studio / Executive Apt",
  DELUXE:   "Deluxe Single / Premium Twin",
  SHARING:  "Premium Shared Residence",
  AC:       "Accessible Premium Room",
  STANDARD: "Standard Room",
};

const PERIOD_LABELS = {
  MONTHLY:  "Monthly",
  SEMESTER: "Per Semester",
  ANNUAL:   "Annual",
};

const APPROVAL_CONFIG = {
  APPROVED: { label: "Approved",   color: "bg-emerald-500/20 text-emerald-300 border-emerald-500/30" },
  DRAFT:    { label: "Draft",      color: "bg-slate-500/20 text-slate-300 border-slate-500/30" },
  PENDING:  { label: "Pending",    color: "bg-amber-500/20 text-amber-300 border-amber-500/30" },
  REJECTED: { label: "Rejected",   color: "bg-red-500/20 text-red-300 border-red-500/30" },
};

const CATEGORY_COLORS = {
  SUITE:    "from-amber-900/40 to-slate-800/60 border-amber-700/40",
  DELUXE:   "from-blue-900/40 to-slate-800/60 border-blue-700/40",
  SHARING:  "from-emerald-900/40 to-slate-800/60 border-emerald-700/40",
  AC:       "from-purple-900/40 to-slate-800/60 border-purple-700/40",
  STANDARD: "from-slate-800/60 to-slate-800/60 border-slate-700/40",
};

export default function HostelRateMaster() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [rates, setRates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [approving, setApproving] = useState(null);

  const institutionId = user?.institution_id || VCE_INST;

  const load = () => {
    if (!token) return;
    setLoading(true);
    axios
      .get(`${API}/api/v1/hostel/rates?institution_id=${institutionId}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((r) => { setRates(r.data?.rates || r.data || []); setLoading(false); })
      .catch(() => setLoading(false));
  };

  useEffect(load, [token, institutionId]);

  const handleApprove = async (rateId, rateName) => {
    setApproving(rateId);
    try {
      await axios.patch(
        `${API}/api/v1/hostel/rates/${rateId}/approve`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Rate "${rateName}" approved`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Approval failed");
    } finally {
      setApproving(null);
    }
  };

  // Group by category
  const grouped = rates.reduce((acc, r) => {
    const cat = r.room_category || "STANDARD";
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(r);
    return acc;
  }, {});

  return (
    <div className="p-6 space-y-5" data-testid="hostel-rate-master">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate("/campus/hostel")}
            className="text-slate-400 hover:text-white" data-testid="btn-back-dashboard">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <Star className="w-5 h-5 text-amber-400" />
              <h1 className="text-xl font-bold text-white">Accommodation Rate Master</h1>
            </div>
            <p className="text-slate-400 text-sm">Kaveri Bhavan · {rates.length} tariffs defined</p>
            <p className="text-xs text-slate-500">[DEMO] Illustrative INR tariffs — not the actual VCE fee structure</p>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12 text-slate-400">
          <Star className="w-8 h-8 mx-auto mb-2 animate-pulse" />
          Loading tariffs…
        </div>
      ) : (
        <div className="space-y-6">
          {Object.entries(grouped).map(([cat, catRates]) => (
            <div key={cat} data-testid={`rate-category-${cat}`}>
              <h2 className="text-sm font-semibold text-slate-300 mb-3 flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-amber-400" />
                {CATEGORY_LABELS[cat] || cat}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {catRates.map((rate) => (
                  <RateCard
                    key={rate.id}
                    rate={rate}
                    onApprove={() => handleApprove(rate.id, rate.name)}
                    approving={approving === rate.id}
                    catColor={CATEGORY_COLORS[cat] || CATEGORY_COLORS.STANDARD}
                  />
                ))}
              </div>
            </div>
          ))}
          {rates.length === 0 && (
            <div className="text-center py-16 text-slate-500">No rates defined yet.</div>
          )}
        </div>
      )}
    </div>
  );
}

function RateCard({ rate, onApprove, approving, catColor }) {
  const ac = APPROVAL_CONFIG[rate.approval_status] || { label: rate.approval_status, color: "bg-slate-500/20 text-slate-300" };
  const services = Array.isArray(rate.services_included) ? rate.services_included : [];

  return (
    <div
      data-testid={`rate-card-${rate.id}`}
      className={`bg-gradient-to-br ${catColor} border rounded-xl p-5 space-y-4`}
    >
      {/* Title Row */}
      <div className="flex items-start justify-between">
        <div>
          <p className="font-semibold text-white">{rate.name}</p>
          <p className="text-xs text-slate-400 mt-0.5">{rate.rate_code}</p>
        </div>
        <Badge className={`text-xs border ${ac.color}`}>{ac.label}</Badge>
      </div>

      {/* Pricing */}
      <div className="bg-black/20 rounded-lg p-3">
        <div className="flex items-baseline gap-1 mb-1">
          <IndianRupee className="w-4 h-4 text-amber-300" />
          <span className="text-2xl font-bold text-white">
            {(rate.rate_amount || 0).toLocaleString("en-IN")}
          </span>
          <span className="text-xs text-slate-400">/ {PERIOD_LABELS[rate.billing_period] || rate.billing_period}</span>
        </div>
        <div className="flex items-center gap-2 text-xs text-slate-400">
          <Shield className="w-3 h-3 text-blue-400" />
          Deposit: ₹{(rate.security_deposit || 0).toLocaleString("en-IN")}
        </div>
        <div className="flex items-center gap-2 text-xs text-slate-400 mt-1">
          <Calendar className="w-3 h-3 text-slate-400" />
          Effective: {rate.effective_from || "—"}
        </div>
      </div>

      {/* Services */}
      {services.length > 0 && (
        <div>
          <p className="text-xs font-medium text-slate-300 mb-2">Included Services</p>
          <div className="flex flex-wrap gap-1">
            {services.slice(0, 4).map((s) => (
              <span key={s} className="text-xs bg-white/5 text-slate-300 px-2 py-0.5 rounded-full flex items-center gap-1">
                <CheckCircle2 className="w-2.5 h-2.5 text-emerald-400" />
                {s.length > 24 ? s.slice(0, 22) + "…" : s}
              </span>
            ))}
            {services.length > 4 && (
              <span className="text-xs text-slate-500 px-2 py-0.5">+{services.length - 4} more</span>
            )}
          </div>
        </div>
      )}

      {/* Occupancy & Billing */}
      <div className="flex gap-2 text-xs">
        <span className="bg-white/5 text-slate-400 px-2 py-1 rounded-full">
          {rate.occupancy_type}
        </span>
        <span className="bg-white/5 text-slate-400 px-2 py-1 rounded-full">
          {rate.billing_unit?.replace("_", " ") || "PER BED"}
        </span>
        <span className="bg-white/5 text-slate-400 px-2 py-1 rounded-full">
          {rate.currency_code || "INR"}
        </span>
      </div>

      {/* Approve action for DRAFT rates */}
      {rate.approval_status === "DRAFT" && (
        <Button
          size="sm"
          className="w-full bg-emerald-600 hover:bg-emerald-500 text-white"
          onClick={onApprove}
          disabled={approving}
          data-testid={`btn-approve-rate-${rate.id}`}
        >
          {approving ? "Approving…" : "Approve Rate"}
        </Button>
      )}

      {rate.approval_status === "APPROVED" && (
        <div className="flex items-center gap-2 text-xs text-slate-400">
          <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
          <span>Approved by {rate.approved_by || "Admin"}</span>
        </div>
      )}

      {rate.notes && (
        <p className="text-xs text-slate-500 italic border-t border-white/10 pt-2">{rate.notes}</p>
      )}
    </div>
  );
}
